<template>
  <div class="container-fluid">
    <!-- Statistics Cards -->
    <div class="row mb-4">
      <div class="col-12">
        <h2 class="mb-3">
          <i class="bi bi-speedometer2 me-2"></i>Dashboard
        </h2>
      </div>
    </div>

    <!-- Overall Statistics -->
    <div class="row g-3 mb-4">
      <div class="col-md-4">
        <div class="card text-bg-primary h-100">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 opacity-75">
              <i class="bi bi-calendar-day me-1"></i>Today
            </h6>
            <h2 class="card-title mb-0">{{ stats.calls_today }}</h2>
            <p class="card-text mb-0">Total Calls</p>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card text-bg-success h-100">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 opacity-75">
              <i class="bi bi-calendar-month me-1"></i>This Month
            </h6>
            <h2 class="card-title mb-0">{{ stats.calls_this_month }}</h2>
            <p class="card-text mb-0">Total Calls</p>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card text-bg-info h-100">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 opacity-75">
              <i class="bi bi-calendar-range me-1"></i>This Year
            </h6>
            <h2 class="card-title mb-0">{{ stats.calls_this_year }}</h2>
            <p class="card-text mb-0">Total Calls</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Department Statistics -->
    <div class="row mb-4" v-if="stats.department_stats && stats.department_stats.length > 0">
      <div class="col-12">
        <h4 class="mb-3">
          <i class="bi bi-building me-2"></i>Department Statistics
        </h4>
      </div>
      <div class="col-lg-3 col-md-6 mb-3" v-for="dept in stats.department_stats" :key="dept.department_id">
        <div class="card h-100">
          <div class="card-header bg-secondary text-white">
            <h6 class="mb-0">{{ dept.department_name }}</h6>
          </div>
          <div class="card-body">
            <div class="row text-center">
              <div class="col-4">
                <div class="border-end">
                  <h5 class="text-primary mb-0">{{ dept.calls_today }}</h5>
                  <small class="text-muted">Day</small>
                </div>
              </div>
              <div class="col-4">
                <div class="border-end">
                  <h5 class="text-success mb-0">{{ dept.calls_this_month }}</h5>
                  <small class="text-muted">Month</small>
                </div>
              </div>
              <div class="col-4">
                <h5 class="text-info mb-0">{{ dept.calls_this_year }}</h5>
                <small class="text-muted">Year</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent Calls -->
    <div class="row">
      <div class="col-12">
        <h4 class="mb-3">
          <i class="bi bi-clock-history me-2"></i>Recent Calls
        </h4>
      </div>
    </div>

    <div class="row g-3">
      <div class="col-12" v-for="call in calls" :key="call.id">
        <div class="card">
          <div class="card-header bg-light">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <i class="bi bi-geo-alt-fill text-danger me-2"></i>
                <a :href="'/call/'+call.id" class="text-decoration-none fw-bold">
                  {{ call.street }}, {{ call.city }}
                </a>
              </div>
              <div class="d-flex align-items-center gap-3">
                <span class="text-muted">
                  <i class="bi bi-calendar3 me-1"></i>
                  {{ formatDate(call.created_at) }}
                </span>
                <button
                  @click="deleteCall(call.id)"
                  class="btn btn-sm btn-outline-danger"
                  title="Delete call">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
            <div class="mt-2">
              <span class="badge bg-primary me-2">
                <i class="bi bi-fire me-1"></i>{{ call.call_type.name }}
              </span>
              <span class="badge bg-secondary">
                <i class="bi bi-building me-1"></i>{{ call.department.short_name }}
              </span>
            </div>
          </div>
          <div class="card-body">
            <div class="row g-2">
              <div class="col-lg-2 col-md-3 col-sm-4 col-6" v-for="equipment in call.equipment" :key="equipment.id">
                <div class="card h-100">
                  <div class="card-body p-2 text-center">
                    <div class="fw-bold">{{ equipment.short_name }}</div>
                    <small class="text-muted d-block">{{ equipment.department?.short_name }}</small>
                  </div>
                  <div class="card-footer p-1 text-center"
                       :class="{
                         'text-bg-danger': equipment.status.id === 1,
                         'text-bg-success': equipment.status.id === 2,
                         'text-bg-secondary': equipment.status.id === 3,
                         'text-bg-info': equipment.status.id === 4
                       }">
                    <small>{{ equipment.status.name }}</small>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="call.equipment.length === 0" class="text-muted text-center py-2">
              <i class="bi bi-inbox"></i> No equipment assigned
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="calls.length === 0 && !isLoading" class="alert alert-info text-center mt-4">
      <i class="bi bi-info-circle me-2"></i>No recent calls to display
    </div>

    <div v-if="isLoading" class="text-center mt-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import axios from "axios";
import moment from "moment";

export default defineComponent({
  name: 'DashboardView',
  components: {},
  data() {
    return {
      calls: [],
      stats: {
        calls_today: 0,
        calls_this_month: 0,
        calls_this_year: 0,
        department_stats: []
      },
      isLoading: true
    };
  },
  mounted() {
    this.loadDashboardData();
  },
  computed: {
  },
  methods: {
    async loadDashboardData() {
      this.isLoading = true;
      try {
        // Load statistics
        const statsResponse = await axios.get('/call/dashboard/stats');
        this.stats = statsResponse.data;

        // Load recent calls
        const callsResponse = await axios.get('/calls/list');
        this.calls = callsResponse.data;
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async deleteCall(callId) {
      if (!confirm('Are you sure you want to delete this call? The record will be kept but hidden from view.')) {
        return;
      }

      try {
        await axios.post('/call/delete', { id: callId });
        // Reload dashboard data to reflect the deletion
        await this.loadDashboardData();
      } catch (error) {
        console.error('Error deleting call:', error);
        alert('Failed to delete call. Please try again.');
      }
    },
    formatDate(dateString) {
      return moment(dateString).format('MMM D, YYYY HH:mm');
    }
  },
});
</script>

<style scoped>
.card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.border-end {
  border-right: 1px solid rgba(0,0,0,0.1);
}
</style>
